import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LogIn, LogOut, UserPlus, User as UserIcon } from 'lucide-react';
import Logo from '@/components/Logo';
import { getCurrentUser, logoutUser, setSession } from '@/lib/api'; // ⚠️ vient maintenant de lib/api
import { getMe, login, fetchWishlists, fetchAccessRequests } from '@/lib/api'; // fetchAccessRequests pour badge admin
import { Button } from '@/components/ui/button';
import { dbg } from '@/lib/logger';

const Header = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    const u = getCurrentUser();
    return u?.user ?? u ?? null; // normalise { user: {...} } -> {...}
  });
  const [sourcingCount, setSourcingCount] = useState(0); // number of wishlists with status 'Sourcing Request Sent'
  const [accessRequestsCount, setAccessRequestsCount] = useState(0); // pending professional access requests
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // <-- ajout état menu mobile

  // Au montage: si un token existe, revalide côté API et resynchronise l'utilisateur
  useEffect(() => {
  let mounted = true;

  async function syncSession() {
    // 👉 Ne tente /auth/me que si un token existe
    const token = localStorage.getItem('tdv_auth_token');
    if (!token) {
      if (mounted) setUser(null);
      return;
    }
    try {
      const me = await getMe();
      if (!mounted) return;
      const normalized = me?.user ?? me ?? null;
      // Ajoute le token si présent dans la réponse (pour garder la session à jour)
      if (me.token) {
        setSession({ user: normalized, token: me.token });
      } else {
        setSession({ user: normalized }); // ne touche pas au token existant
      }
      setUser(normalized);
      dbg('Header syncSession normalized:', normalized);
    } catch {
      // http() fait déjà un logout() auto sur 401, on reflète l’état
      if (mounted) setUser(null);
    }
  }

    // 1) essai de sync immédiat
    syncSession();

    // 2) écoute les changements de session (login/logout)
    const onAuthChange = () => {
      const u = getCurrentUser();
      setUser(u?.user ?? u ?? null); // normalise { user: {...} } -> {...}
    };
    window.addEventListener('authChange', onAuthChange);
    window.addEventListener('storage', onAuthChange);

    return () => {
      mounted = false;
      window.removeEventListener('authChange', onAuthChange);
      window.removeEventListener('storage', onAuthChange);
    };
  }, []);

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  const navItems = [
    
    { to: '/catalogue', text: 'Catalogue', requiresAuth: true },
    { to: '/wishlists', text: 'Project Boards', requiresAuth: true },
    { to: '/admin', text: 'Admin', requiresAuth: true, adminOnly: true },
  ];

  // Normalize user and derive admin before effects to avoid TDZ issues.
  const normalized = user?.user ?? user ?? null;
  const role = normalized?.role ?? normalized?.role_name ?? normalized?.user?.role ?? normalized?.user?.role_name;
  const isAdmin = Boolean(
    (role && String(role).toLowerCase() === 'admin') || normalized?.role_id === 1
  );
  const isDesignerOrAdmin = Boolean(
    (role && (String(role).toLowerCase() === 'designer' || String(role).toLowerCase() === 'admin')) || normalized?.role_id === 1
  );
  // Access requests badge effect (runs after isAdmin computed)
  useEffect(() => {
    let abort = false;
    async function loadAccessRequests() {
      if (!isAdmin) { setAccessRequestsCount(0); return; }
      // Ensure there is still a logged-in user / token before calling protected endpoint
      const token = localStorage.getItem('tdv_auth_token');
      if (!token) { setAccessRequestsCount(0); return; }
      try {
        const reqs = await fetchAccessRequests();
        if (abort) return;
        const pending = (reqs || []).filter(r => {
          const st = (r.status || r.state || '').toLowerCase();
          return !st || st === 'pending' || st === 'requested';
        }).length;
        setAccessRequestsCount(pending);
      } catch (e) {
        if (!abort) setAccessRequestsCount(0);
      }
    }
    loadAccessRequests();
    const onAuthChange = () => loadAccessRequests();
    const visibilityHandler = () => { if (document.visibilityState === 'visible') loadAccessRequests(); };
    window.addEventListener('authChange', onAuthChange);
    document.addEventListener('visibilitychange', visibilityHandler);
    return () => {
      abort = true;
      window.removeEventListener('authChange', onAuthChange);
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, [isAdmin]);
  dbg('Header role:', role, 'role_id:', user?.role_id, 'isAdmin:', isAdmin);

  // Load sourcing request count for admins
  useEffect(() => {
    let abort = false;
    async function loadSourcingCount() {
      if (!isAdmin) {
        setSourcingCount(0);
        return;
      }
      const token = localStorage.getItem('tdv_auth_token');
      if (!token) { setSourcingCount(0); return; }
      try {
        const wls = await fetchWishlists();
        if (abort) return;
        const count = (wls || []).filter(w => w.status === 'Sourcing Request Sent').length;
        setSourcingCount(count);
      } catch (e) {
        if (!abort) setSourcingCount(0);
      }
    }
    loadSourcingCount();
    const onAuthChange = () => loadSourcingCount();
    window.addEventListener('authChange', onAuthChange);
    const visibilityHandler = () => { if (document.visibilityState === 'visible') loadSourcingCount(); };
    document.addEventListener('visibilitychange', visibilityHandler);
    return () => {
      abort = true;
      window.removeEventListener('authChange', onAuthChange);
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, [isAdmin]);

  return (
    <header className="sticky top-0 z-40 bg-card/80 backdrop-blur-lg border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-24 py-2">
          <NavLink to="/" className="flex items-center space-x-3">
          <Logo className="h-16 md:h-20 lg:h-24" />
            {/* Hide brand text and tagline on mobile */}
            <div className="flex flex-col hidden sm:flex">
              <span
                className="brand-font text-lg md:text-2xl font-semibold select-none tracking-widest"
                style={{ letterSpacing: '0.21em' }}
              >
                The Design Vault
              </span>
              <span
                className="text-xs md:text-xsm text-muted-foreground select-none tracking-widest"
                style={{ letterSpacing: '0.04em' }}
              >
                EFFORTLESS SOURCING FOR PROFESSIONALS
              </span>
            </div>
          </NavLink>

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-8 justify-center flex-1">
            {(() => {
              const visible = navItems.filter(item => {
                if (item.adminOnly) return isAdmin;
                if (item.requiresAuth) return !!user;
                return true;
              });
              return visible;
            })().map(item => {
              const isBoards = item.to === '/wishlists' && isAdmin && sourcingCount >0;
              const isAdminLink = item.to === '/admin' && isAdmin && accessRequestsCount >0;
              // Compute label so '/catalogue' shows 'Inspiration' to anonymous users
                // Hide 'Catalogue'/'Inspiration' if not logged in
                // Only show the catalogue link to designers or admins
                if (item.to === '/catalogue' && !isDesignerOrAdmin) return null;
                const label = item.to === '/catalogue' ? (isDesignerOrAdmin ? 'Inspiration' : 'Inspiration') : item.text;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `relative font-medium text-sm tracking-wide transition-colors after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:w-full after:bg-primary after:scale-x-0 after:origin-left after:transition-transform hover:text-primary hover:after:scale-x-100 ${
                      isActive ? 'text-primary after:scale-x-100' : 'text-muted-foreground'
                    }`
                  }
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {label}
                  {isBoards && (
                    <span className="absolute -top-3 -right-4 min-w-[20px] h-5 px-1.5 rounded-full bg-purple-200 text-purple-800 text-[11px] font-semibold flex items-center justify-center shadow-sm ring-1 ring-purple-300">
                      {sourcingCount}
                    </span>
                  )}
                  {isAdminLink && (
                    <span className="absolute -top-3 -right-4 min-w-[20px] h-5 px-1.5 rounded-full bg-blue-500/90 text-white text-[11px] font-semibold flex items-center justify-center shadow-sm ring-1 ring-blue-300">
                      {accessRequestsCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2 justify-end">
            {user && user.name ? (
              <>
                <span className="hidden sm:inline text-sm font-medium text-muted-foreground">
                  Welcome, {user.name.split(' ')[0]}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  title="My Profile"
                  className="text-muted-foreground hover:text-primary"
                >
                  <NavLink to="/designer-profile">
                    <UserIcon className="w-5 h-5" />
                  </NavLink>
                </Button>
                <Button variant="ghost" size="icon" onClick={handleLogout} title="Logout">
                  <LogOut className="w-5 h-5" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" asChild className="hidden sm:inline-flex">
                  <NavLink to="/login">Login</NavLink>
                </Button>
                <Button asChild className="hidden sm:inline-flex">
                  <NavLink to="/request-access">
                    <UserPlus className="w-5 h-5 mr-2" />
                    Request Pro Access
                  </NavLink>
                </Button>
              </>
            )}

            {/* Hamburger button (mobile) */}
            <button
              type="button"
              aria-label="Toggle navigation"
              aria-expanded={isMobileMenuOpen}
              className="sm:hidden inline-flex flex-col justify-between w-9 h-7 p-1.5 rounded-md border border-border bg-background hover:bg-muted transition-colors"
              onClick={() => setIsMobileMenuOpen(o => !o)}
            >
              <span className={`h-[2px] w-full rounded bg-current transition-transform ${isMobileMenuOpen ? 'translate-y-[5px] rotate-45' : ''}`}></span>
              <span className={`h-[2px] w-full rounded bg-current transition-opacity ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`h-[2px] w-full rounded bg-current transition-transform ${isMobileMenuOpen ? '-translate-y-[5px] -rotate-45' : ''}`}></span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {isMobileMenuOpen && (
        <div className="sm:hidden bg-background/95 backdrop-blur-md border-b border-border px-4 pb-4" role="dialog" aria-label="Mobile navigation">
          <div className="flex flex-col gap-2 pt-2">
            {(navItems.filter(item => {
              if (item.adminOnly) return isAdmin;
              if (item.requiresAuth) return !!user;
              return true;
            })).map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `px-3 py-2 rounded-md text-sm font-medium flex items-center justify-between ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span>{item.to === '/catalogue' ? (user ? 'Catalogue' : 'Inspiration') : item.text}</span>
                  {item.to === '/wishlists' && isAdmin && sourcingCount >0 && (
                    <span className="ml-2 inline-flex min-w-[20px] h-5 px-1.5 rounded-full bg-purple-200 text-purple-800 text-[11px] font-semibold items-center justify-center">
                      {sourcingCount}
                    </span>
                  )}
                  {item.to === '/admin' && isAdmin && accessRequestsCount >0 && (
                    <span className="ml-2 inline-flex min-w-[20px] h-5 px-1.5 rounded-full bg-blue-500/90 text-white text-[11px] font-semibold items-center justify-center">
                      {accessRequestsCount}
                    </span>
                  )}
                </NavLink>
            ))}
            {/* Mobile auth actions if logged out */}
            {!(user && user.name) && (
              <div className="flex gap-2 mt-2">
                <NavLink to="/login" className="flex-1 px-3 py-2 rounded-md text-sm font-medium text-center bg-muted hover:bg-muted/70" onClick={() => setIsMobileMenuOpen(false)}>Login</NavLink>
                <NavLink to="/request-access" className="flex-1 px-3 py-2 rounded-md text-sm font-medium text-center bg-primary text-primary-foreground hover:brightness-110" onClick={() => setIsMobileMenuOpen(false)}>Pro Access</NavLink>
              </div>
            )}
            {user && user.name && (
              <div className="flex gap-2 mt-2">
                <NavLink to="/designer-profile" className="flex-1 px-3 py-2 rounded-md text-sm font-medium text-center bg-muted hover:bg-muted/70" onClick={() => setIsMobileMenuOpen(false)}>Profile</NavLink>
                <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} className="flex-1 px-3 py-2 rounded-md text-sm font-medium text-center bg-destructive text-destructive-foreground hover:brightness-110">Logout</button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
